import { createPortal } from 'react-dom';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Icon } from '../lib/icons';

export function Dialog({ title, onClose, children, footer, wide }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    ref.current?.querySelector<HTMLElement>('input, textarea, button[data-autofocus]')?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  // Portal, damit transformierte Eltern (Composer, Panels) die Position nicht verschieben
  return createPortal(
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`dialog ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="m-0 text-[19px] font-semibold tracking-tight">{title}</h2>
          <button className="iconbtn plain" onClick={onClose} aria-label="Schließen"><Icon name="x" size={20} /></button>
        </div>
        {children}
        {footer && <div className="mt-6 flex justify-end gap-2">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Tooltips für alle Elemente mit `data-tip`. Eine einzige Blase, fest im
 * Fenster: sie misst den Platz und weicht aus (oben, unten, rechts, links),
 * statt am Rand abgeschnitten oder von Eltern mit eigenem Bildlauf gekappt
 * zu werden. `data-tip-below` bevorzugt unten, in der schmalen Leiste rechts.
 */
export function TooltipLayer() {
  const [tip, setTip] = useState<{ text: string; wrap: boolean; x: number; y: number; side: string } | null>(null);
  const bubble = useRef<HTMLDivElement>(null);
  const target = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const show = (e: Event) => {
      const el = (e.target as HTMLElement | null)?.closest?.('[data-tip]') as HTMLElement | null;
      if (!el || el === target.current) return;
      const text = el.getAttribute('data-tip');
      if (!text) return;
      target.current = el;
      setTip({ text, wrap: el.hasAttribute('data-tip-wrap'), x: -9999, y: -9999, side: 'top' });
    };
    const hide = (e: Event) => {
      const el = target.current;
      const to = (e as MouseEvent).relatedTarget as Node | null;
      if (!el || (to && el.contains(to))) return;
      target.current = null;
      setTip(null);
    };
    const clear = () => {
      target.current = null;
      setTip(null);
    };
    document.addEventListener('mouseover', show);
    document.addEventListener('mouseout', hide);
    document.addEventListener('focusin', show);
    document.addEventListener('focusout', clear);
    document.addEventListener('mousedown', clear);
    window.addEventListener('scroll', clear, true);
    return () => {
      document.removeEventListener('mouseover', show);
      document.removeEventListener('mouseout', hide);
      document.removeEventListener('focusin', show);
      document.removeEventListener('focusout', clear);
      document.removeEventListener('mousedown', clear);
      window.removeEventListener('scroll', clear, true);
    };
  }, []);

  // Nach dem ersten Rendern messen und die Seite wählen, auf der Platz ist
  useLayoutEffect(() => {
    const el = target.current;
    const b = bubble.current;
    if (!tip || !el || !b || tip.x !== -9999) return;
    const r = el.getBoundingClientRect();
    const w = b.offsetWidth;
    const h = b.offsetHeight;
    const gap = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const order = el.closest('.railbar')
      ? ['right', 'bottom', 'top', 'left']
      : el.hasAttribute('data-tip-below')
        ? ['bottom', 'top', 'right', 'left']
        : ['top', 'bottom', 'right', 'left'];
    const place = (side: string) => {
      switch (side) {
        case 'top': return { x: r.left + r.width / 2 - w / 2, y: r.top - gap - h };
        case 'bottom': return { x: r.left + r.width / 2 - w / 2, y: r.bottom + gap };
        case 'right': return { x: r.right + gap, y: r.top + r.height / 2 - h / 2 };
        default: return { x: r.left - gap - w, y: r.top + r.height / 2 - h / 2 };
      }
    };
    const fits = (p: { x: number; y: number }, side: string) =>
      side === 'top' || side === 'bottom' ? p.y >= 4 && p.y + h <= vh - 4 : p.x >= 4 && p.x + w <= vw - 4;
    const side = order.find((sd) => fits(place(sd), sd)) ?? order[0]!;
    const p = place(side);
    // Am Rand entlang schieben, damit nichts herausragt
    const x = Math.min(Math.max(4, p.x), vw - w - 4);
    const y = Math.min(Math.max(4, p.y), vh - h - 4);
    setTip({ ...tip, x, y, side });
  }, [tip]);

  if (!tip) return null;
  return createPortal(
    <div
      ref={bubble}
      role="tooltip"
      className={`tipbubble ${tip.wrap ? 'wrap' : ''}`}
      data-side={tip.side}
      style={{ left: tip.x, top: tip.y, visibility: tip.x === -9999 ? 'hidden' : 'visible' }}
    >
      {tip.text}
    </div>,
    document.body,
  );
}

/**
 * Eigenes Auswahlmenü, an einem Auslöser verankert. Das Menü hängt als Portal
 * am Körper der Seite und steht fest im Fenster: sonst schneiden Eltern mit
 * eigenem Bildlauf (Filterleiste, Listen) es ab.
 */
export function Popover({ trigger, children, align = 'left', width, up }: {
  trigger: (props: { open: boolean; toggle: () => void }) => ReactNode;
  children: (close: () => void) => ReactNode;
  align?: 'left' | 'right';
  width?: number;
  /** Nach oben öffnen (z. B. über der Eingabeleiste) */
  up?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState<CSSProperties | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 6;
    const style: CSSProperties = { position: 'fixed', width };
    if (align === 'right') style.right = Math.max(8, window.innerWidth - r.right);
    else style.left = Math.max(8, r.left);
    if (up) {
      style.bottom = Math.max(8, window.innerHeight - r.top + gap);
      style.maxHeight = Math.max(160, r.top - gap - 16);
    } else {
      style.top = r.bottom + gap;
      style.maxHeight = Math.max(160, window.innerHeight - r.bottom - gap - 16);
    }
    setBox(style);
  }, [align, up, width]);

  useLayoutEffect(() => {
    if (!open) return setBox(null);
    place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const inside = (t: Node) => !!ref.current?.contains(t) || !!menu.current?.contains(t);
    const onDown = (e: MouseEvent) => !inside(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    // Beim Blättern schließt das Menü (wie im Messenger); im Menü selbst nicht
    const onScroll = (e: Event) => {
      if (menu.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onResize = () => place();
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, place]);

  return (
    <div className="relative" ref={ref}>
      {trigger({ open, toggle: () => setOpen((o) => !o) })}
      {open &&
        box &&
        createPortal(
          <div className="menu" role="menu" ref={menu} style={box}>
            {children(() => setOpen(false))}
          </div>,
          document.body,
        )}
    </div>
  );
}

/** Auswahlfeld als Ersatz für <select>. */
export function Picker<T extends string>({ value, options, onChange, placeholder = 'Auswählen', label }: {
  value: T | null;
  options: { value: T; label: string; hint?: string }[];
  onChange: (v: T) => void;
  placeholder?: string;
  label: string;
}) {
  const current = options.find((o) => o.value === value);
  return (
    <div className="field">
      <span>{label}</span>
      <Popover
        width={undefined}
        trigger={({ toggle, open }) => (
          <button type="button" className="input flex w-full items-center justify-between text-left" onClick={toggle} aria-haspopup="listbox" aria-expanded={open}>
            <span className={current ? '' : 'text-[var(--dim)]'}>{current?.label ?? placeholder}</span>
            <Icon name="down" size={18} className="text-[var(--muted)]" />
          </button>
        )}
      >
        {(close) => (
          <div role="listbox" className="max-h-[320px] w-full overflow-y-auto">
            {options.map((o) => (
              <button key={o.value} type="button" className="item" role="option" aria-selected={o.value === value} onClick={() => (onChange(o.value), close())}>
                <span className="flex-1">
                  <span className="block font-medium">{o.label}</span>
                  {o.hint && <span className="block text-xs text-[var(--muted)]">{o.hint}</span>}
                </span>
                {o.value === value && <Icon name="check" size={18} className="text-[var(--brand-a)]" />}
              </button>
            ))}
          </div>
        )}
      </Popover>
    </div>
  );
}

const ToastCtx = createContext<(msg: string) => void>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [msg, setMsg] = useState<string | null>(null);
  const timer = useRef<number>(undefined);
  const show = useCallback((m: string) => {
    setMsg(m);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setMsg(null), 3200);
  }, []);
  return (
    <ToastCtx.Provider value={show}>
      {/* Jede Oberfläche hat einen ToastProvider — die Tooltips hängen mit daran */}
      <TooltipLayer />
      {children}
      {msg && <div className="toast" role="status">{msg}</div>}
    </ToastCtx.Provider>
  );
}

/** Rundes Bild mit Initialen als Rückfall (Profilbild, Projektbild, Logo). */
export function Avatar({ name, size = 36, gradient, src, square }: { name: string; size?: number; gradient: string; src?: string | null; square?: boolean }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  const radius = square ? size * 0.3 : '50%';
  if (src && !broken) {
    return (
      <img
        className="av object-cover"
        src={src}
        alt=""
        width={size}
        height={size}
        style={{ width: size, height: size, borderRadius: radius }}
        onError={() => setBroken(true)}
      />
    );
  }
  return (
    <span className="av" style={{ width: size, height: size, fontSize: size * 0.36, background: gradient, borderRadius: radius }} aria-hidden="true">
      {name}
    </span>
  );
}

/**
 * Quadratischen Ausschnitt eines Bildes wählen: schieben, zoomen, dann als JPEG zuschneiden.
 * Ohne Fremdbibliothek, der Zuschnitt läuft über ein Canvas.
 */
export function ImageCropDialog({ file, square, onCancel, onDone }: {
  file: File;
  /** Maske eckig statt rund (Projektbild, Logo) */
  square?: boolean;
  onCancel: () => void;
  onDone: (file: File) => void;
}) {
  const view = 272; // Kantenlänge des Ausschnittfensters in Bildpunkten
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const el = new Image();
    let alive = true; // ein abgebrochener Ladevorgang darf keinen Fehler melden
    setFailed(false);
    el.onload = () => alive && (setImg(el), setFailed(false));
    el.onerror = () => alive && setFailed(true);
    el.src = url;
    return () => {
      alive = false;
      URL.revokeObjectURL(url);
    };
  }, [file]);

  // Escape schließt nur diesen Dialog, nicht den darunterliegenden
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onCancel();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  // Maßstab, bei dem das Bild das Fenster gerade ausfüllt
  const base = img ? view / Math.min(img.naturalWidth, img.naturalHeight) : 1;
  const shown = img ? { w: img.naturalWidth * base * zoom, h: img.naturalHeight * base * zoom } : { w: 0, h: 0 };

  /** Das Fenster darf nie über den Bildrand hinausragen. */
  const clamp = useCallback(
    (x: number, y: number, w: number, h: number) => {
      const maxX = Math.max(0, (w - view) / 2);
      const maxY = Math.max(0, (h - view) / 2);
      return { x: Math.min(maxX, Math.max(-maxX, x)), y: Math.min(maxY, Math.max(-maxY, y)) };
    },
    [view],
  );

  function changeZoom(next: number) {
    const z = Math.min(6, Math.max(1, next));
    if (!img) return setZoom(z);
    const factor = z / zoom;
    const w = img.naturalWidth * base * z;
    const h = img.naturalHeight * base * z;
    setZoom(z);
    setOffset((o) => clamp(o.x * factor, o.y * factor, w, h));
  }

  async function apply() {
    if (!img) return;
    setBusy(true);
    try {
      // Ausschnitt in Bildpunkten des Originals
      const src = view / (base * zoom);
      const sx = img.naturalWidth / 2 - offset.x / (base * zoom) - src / 2;
      const sy = img.naturalHeight / 2 - offset.y / (base * zoom) - src / 2;
      const out = Math.max(256, Math.min(1024, Math.round(src)));
      const canvas = document.createElement('canvas');
      canvas.width = out;
      canvas.height = out;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('kein Canvas');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, out, out);
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, sx, sy, src, src, 0, 0, out, out);
      const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', 0.9));
      if (!blob) throw new Error('kein Bild');
      onDone(new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }));
    } catch {
      onDone(file); // im Zweifel das Original hochladen, der Server schneidet mittig zu
    }
  }

  const disabled = !img || busy;
  return (
    <Dialog title="Bild zuschneiden" onClose={onCancel}>
      <div className="flex flex-col items-center gap-4">
        <div
          className="relative touch-none overflow-hidden rounded-2xl bg-[var(--surface2)] select-none"
          style={{ width: view, height: view, cursor: img ? 'grab' : 'default' }}
          onPointerDown={(e) => {
            if (!img) return;
            try {
              e.currentTarget.setPointerCapture(e.pointerId);
            } catch {
              /* ohne Zeigerfang geht es auch */
            }
            drag.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
          }}
          onPointerMove={(e) => {
            const d = drag.current;
            if (!d) return;
            setOffset(clamp(d.ox + (e.clientX - d.x), d.oy + (e.clientY - d.y), shown.w, shown.h));
          }}
          onPointerUp={() => (drag.current = null)}
          onPointerCancel={() => (drag.current = null)}
          onWheel={(e) => changeZoom(zoom * (e.deltaY < 0 ? 1.12 : 1 / 1.12))}
        >
          {img && (
            <img
              src={img.src}
              alt=""
              draggable={false}
              className="absolute top-1/2 left-1/2 max-w-none"
              style={{ width: shown.w, height: shown.h, transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))` }}
            />
          )}
          {/* Maske: alles außerhalb des Ausschnitts abdunkeln */}
          <span
            className="pointer-events-none absolute inset-0 shadow-[0_0_0_9999px_rgb(0_0_0/0.45)]"
            style={{ borderRadius: square ? 24 : '50%' }}
            aria-hidden="true"
          />
          {failed && <span className="absolute inset-0 grid place-items-center text-[13.5px] text-[var(--muted)]">Bild konnte nicht gelesen werden</span>}
        </div>

        <label className="flex w-full max-w-[272px] items-center gap-3">
          <span className="sr-only">Größe</span>
          <Icon name="image" size={14} />
          <input
            className="crop-zoom"
            type="range"
            min={1}
            max={6}
            step={0.01}
            value={zoom}
            disabled={!img}
            onChange={(e) => changeZoom(Number(e.target.value))}
          />
          <Icon name="image" size={20} />
        </label>
        <p className="m-0 text-center text-[13px] text-[var(--muted)]">Ziehen zum Verschieben, Regler zum Vergrößern.</p>

        <div className="flex w-full justify-end gap-2">
          <button type="button" className="btn btn-ghost" onClick={onCancel}>Abbrechen</button>
          <button type="button" className="btn btn-primary" disabled={disabled} onClick={() => void apply()}>Übernehmen</button>
        </div>
      </div>
    </Dialog>
  );
}

/** Bildauswahl mit Vorschau, z. B. für Projektbild oder Profilbild. Vor dem Übernehmen wird zugeschnitten. */
export function ImagePicker({ label, preview, fallback, onPick, onRemove, square }: {
  label: string;
  preview: string | null;
  fallback: { name: string; gradient: string };
  onPick: (file: File) => void;
  onRemove?: () => void;
  square?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [cropping, setCropping] = useState<File | null>(null);
  return (
    <div className="flex items-center gap-4">
      <button type="button" className="relative rounded-full" onClick={() => input.current?.click()} aria-label={`${label} wählen`}>
        <Avatar name={fallback.name} gradient={fallback.gradient} src={preview} size={72} square={square} />
        <span className="absolute -right-1 -bottom-1 grid h-7 w-7 place-items-center rounded-full border-2 border-[var(--bg)] bg-[image:var(--brand)] text-white">
          <Icon name="camera" size={14} />
        </span>
      </button>
      <div className="flex flex-col items-start gap-1">
        <span className="text-[13px] font-semibold text-[var(--muted)]">{label}</span>
        <div className="flex gap-2">
          <button type="button" className="chip" onClick={() => input.current?.click()}>{preview ? 'Ändern' : 'Bild wählen'}</button>
          {preview && onRemove && <button type="button" className="chip" onClick={onRemove}>Entfernen</button>}
        </div>
      </div>
      <input
        ref={input}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) setCropping(f);
        }}
      />
      {cropping && (
        <ImageCropDialog
          file={cropping}
          square={square}
          onCancel={() => setCropping(null)}
          onDone={(f) => {
            setCropping(null);
            onPick(f);
          }}
        />
      )}
    </div>
  );
}

/** Logo; mit `claim` steht klein „einfach...“ unter dem Wortzeichen (gelesen: „Mach's einfach...“). */
export function Logo({ size = 32, claim = false }: { size?: number; claim?: boolean }) {
  return (
    <span className="flex items-center gap-2.5">
      <span className="mark" style={{ width: size, height: size }}>
        <Icon name="logo" size={size * 0.6} strokeWidth={2.6} />
      </span>
      {/* Links bündig, auch wenn das Logo in einem Knopf steht (der zentriert sonst) */}
      <span className="text-left leading-none">
        <span className="wordmark block" style={{ fontSize: size * 0.62 }}>Mach<span>'s</span></span>
        {claim && <span className="logo-claim" style={{ fontSize: Math.max(10.5, size * 0.3) }}>einfach...</span>}
      </span>
    </span>
  );
}
